import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Badge } from "../../../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../../ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../../ui/command";
import { Skeleton } from "../../../ui/skeleton";
import { Check, ChevronLeft, ChevronRight, Loader2, Send, Users, Mail, Eye, AlertTriangle, X, UserPlus } from "lucide-react";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { toast } from "sonner";
import { AudienceFilter, AudiencePreset, defaultAudienceFilter, countAudience, describeAudience } from "../../../../lib/audienceEngageV1";
import { STATUS_OPTIONS, STATUS_LABEL } from "../../../../lib/customersEngageV1";
import { EmailPreviewIframe } from "../components/email/EmailPreviewIframe";
import { SelectCustomersDialog } from "../components/customers/SelectCustomersDialog";

type Template = {
  id: string;
  name: string | null;
  subject_line: string | null;
  type: string | null;
  body_html: string | null;
  body_plain: string | null;
};

const STEPS = [
  { id: 1, label: "Audience", icon: Users },
  { id: 2, label: "Template", icon: Mail },
  { id: 3, label: "Preview", icon: Eye },
  { id: 4, label: "Send", icon: Send },
];

export default function NewCampaign() {
  const { merchant, credits, refresh } = useMerchant();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectTemplate = params.get("template");

  const [step, setStep] = useState(1);

  // Audience
  const [audience, setAudience] = useState<AudienceFilter>(defaultAudienceFilter);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [estimated, setEstimated] = useState<number>(0);
  const [counting, setCounting] = useState(false);

  // Templates
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Preview
  const [subjectOverride, setSubjectOverride] = useState<string>("");
  const [campaignName, setCampaignName] = useState<string>("");

  // Sending
  const [sending, setSending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handPicked = (audience.customer_ids?.length ?? 0) > 0;

  const remaining = credits ? Math.max(0, (credits.credits_total ?? 0) - (credits.credits_used ?? 0)) : 0;
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;

  // Load tags + templates once merchant is ready
  useEffect(() => {
    if (!merchant) return;
    (async () => {
      const { data: tags } = await (epicengageSupabasev1 as any)
        .from("active_customer_tags")
        .select("tag")
        .eq("merchant_id", merchant.id);
      const unique = Array.from(new Set(((tags as { tag: string }[] | null) || []).map((t) => t.tag))).sort();
      setAllTags(unique);
    })();
  }, [merchant]);

  useEffect(() => {
    if (!merchant) return;
    setTemplatesLoading(true);
    (async () => {
      const { data } = await epicengageSupabasev1
        .from("email_templates")
        .select("id, name, subject_line, type, body_html, body_plain")
        .eq("merchant_id", merchant.id)
        .order("updated_at", { ascending: false });
      setTemplates(data || []);
      if (preselectTemplate && (data || []).some((t) => t.id === preselectTemplate)) {
        setSelectedTemplateId(preselectTemplate);
      }
      setTemplatesLoading(false);
    })();
  }, [merchant, preselectTemplate]);

  // Recount whenever filters change
  useEffect(() => {
    if (!merchant) return;
    let cancelled = false;
    setCounting(true);
    countAudience(merchant.id, audience)
      .then((n) => {
        if (!cancelled) setEstimated(n);
      })
      .finally(() => !cancelled && setCounting(false));
    return () => {
      cancelled = true;
    };
  }, [merchant, audience]);

  // When template selected, default subject + name
  useEffect(() => {
    if (selectedTemplate) {
      setSubjectOverride(selectedTemplate.subject_line || "");
      setCampaignName((prev) => prev || selectedTemplate.name || "Untitled campaign");
    }
  }, [selectedTemplate]);

  const filteredTemplates = useMemo(
    () => (templateFilter === "all" ? templates : templates.filter((t) => (t.type || "manual") === templateFilter)),
    [templates, templateFilter]
  );

  const canNext = useMemo(() => {
    if (step === 1) return estimated > 0;
    if (step === 2) return !!selectedTemplateId;
    if (step === 3) return subjectOverride.trim().length > 0;
    return true;
  }, [step, estimated, selectedTemplateId, subjectOverride]);

  const enoughCredits = remaining >= estimated;

  const handleSend = async () => {
    if (!merchant || !selectedTemplate) return;
    setSending(true);
    try {
      // Ensure we have a fresh session token (avoid sending stale/anon auth on long-running flows)
      let { data: sessionData } = await epicengageSupabasev1.auth.getSession();
      if (!sessionData.session || (sessionData.session.expires_at ?? 0) * 1000 < Date.now() + 60_000) {
        const { data: refreshed } = await epicengageSupabasev1.auth.refreshSession();
        sessionData = { session: refreshed.session } as typeof sessionData;
      }
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error("Your session expired. Please sign in again.");
        return;
      }

      const { data, error } = await epicengageSupabasev1.functions.invoke("send-campaign", {
        body: {
          merchant_id: merchant.id,
          template_id: selectedTemplate.id,
          audience_filter: audience,
          subject_override: subjectOverride,
          name: campaignName,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (error) {
        const msg = (error as { message?: string }).message || "Send failed";
        toast.error(msg);
        return;
      }
      const total = (data as { total?: number })?.total ?? estimated;
      toast.success(
        `Campaign queued — sending to ${total.toLocaleString()} recipient${total === 1 ? "" : "s"} in the background.`
      );
      await refresh();
      navigate("/clientpanel/enagev1/campaigns");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="px-10 py-10">
        <button
          onClick={() => navigate("/clientpanel/enagev1/campaigns")}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to campaigns
        </button>

        <h1 className="text-2xl font-semibold tracking-tight">New campaign</h1>
        <p className="text-sm text-muted-foreground mt-1">Send an email to your audience in 4 quick steps.</p>

        {/* Stepper */}
        <ol className="mt-8 grid grid-cols-4 gap-2">
          {STEPS.map(({ id, label, icon: Icon }) => {
            const done = step > id;
            const active = step === id;
            return (
              <li
                key={id}
                className={`flex items-center gap-2 rounded-md border p-3 text-sm ${
                  active
                    ? "border-primary bg-primary/5 text-foreground"
                    : done
                    ? "border-emerald-300/60 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200"
                    : "border-border text-muted-foreground"
                }`}
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : done
                      ? "bg-emerald-500 text-white"
                      : "bg-muted"
                  }`}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : id}
                </span>
                <Icon className="h-4 w-4" />
                <span className="font-medium">{label}</span>
              </li>
            );
          })}
        </ol>

        <div className="mt-8">
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Choose your audience</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Quick presets</Label>
                    <Button
                      type="button"
                      variant={handPicked ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPickerOpen(true)}
                    >
                      <UserPlus className="h-4 w-4 mr-1.5" />
                      {handPicked
                        ? `${audience.customer_ids!.length} hand-picked`
                        : "Pick specific customers"}
                    </Button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(
                      [
                        { id: "all", label: "All Customers" },
                        { id: "new", label: "New (last 30d)" },
                        { id: "active", label: "Active" },
                        { id: "inactive", label: "Inactive" },
                      ] as { id: AudiencePreset; label: string }[]
                    ).map((p) => (
                      <Button
                        key={p.id}
                        type="button"
                        variant={!handPicked && audience.preset === p.id ? "default" : "outline"}
                        size="sm"
                        disabled={handPicked}
                        onClick={() => setAudience({ ...audience, preset: p.id })}
                      >
                        {p.label}
                      </Button>
                    ))}
                  </div>
                  {handPicked && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Filters are disabled while sending to a hand-picked list.{" "}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setAudience({ ...audience, customer_ids: [] })}
                      >
                        Clear selection
                      </button>
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Tags (must include all)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start mt-1 font-normal h-auto min-h-10 py-2" disabled={handPicked}>
                          {audience.tags.length === 0 ? (
                            <span className="text-muted-foreground">Search and select tags…</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {audience.tags.map((t) => (
                                <Badge
                                  key={t}
                                  variant="secondary"
                                  className="gap-1 pr-1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setAudience({ ...audience, tags: audience.tags.filter((x) => x !== t) });
                                  }}
                                >
                                  {t}
                                  <X className="h-3 w-3 opacity-60 hover:opacity-100" />
                                </Badge>
                              ))}
                            </div>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search tags…" />
                          <CommandList>
                            <CommandEmpty>No tags found.</CommandEmpty>
                            <CommandGroup>
                              {allTags.map((t) => {
                                const checked = audience.tags.includes(t);
                                return (
                                  <CommandItem
                                    key={t}
                                    value={t}
                                    onSelect={() =>
                                      setAudience({
                                        ...audience,
                                        tags: checked
                                          ? audience.tags.filter((x) => x !== t)
                                          : [...audience.tags, t],
                                      })
                                    }
                                  >
                                    <span
                                      className={`mr-2 h-4 w-4 rounded border flex items-center justify-center ${
                                        checked ? "bg-primary border-primary text-primary-foreground" : "border-input"
                                      }`}
                                    >
                                      {checked && <Check className="h-3 w-3" />}
                                    </span>
                                    {t}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label>Status filter</Label>
                    <Select
                      value={audience.status || "all"}
                      onValueChange={(v) => setAudience({ ...audience, status: v })}
                      disabled={handPicked}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any status</SelectItem>
                        {STATUS_OPTIONS.filter((s) => s !== "unsubscribed").map((s) => (
                          <SelectItem key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>


                <div className="rounded-md border bg-muted/30 p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Estimated recipients</p>
                    <p className="text-2xl font-semibold mt-0.5">
                      {counting ? <Loader2 className="h-5 w-5 animate-spin" /> : estimated.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Excludes unsubscribed customers.</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Credit cost</p>
                    <p className="text-2xl font-semibold mt-0.5">{estimated.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      You have {remaining.toLocaleString()} remaining.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>Pick a template</CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate("/clientpanel/enagev1/templates?new=1")}>
                  + New template
                </Button>
              </CardHeader>
              <CardContent>
                <Tabs value={templateFilter} onValueChange={setTemplateFilter}>
                  <TabsList>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="manual">Manual</TabsTrigger>
                    <TabsTrigger value="campaign">Event</TabsTrigger>
                    <TabsTrigger value="automation">Automation</TabsTrigger>
                  </TabsList>
                </Tabs>

                {templatesLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    {[...Array(4)].map((_, i) => (
                      <Skeleton key={i} className="h-28 w-full" />
                    ))}
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-12 text-center mt-4 space-y-3">
                    <p className="text-sm text-muted-foreground">No templates yet.</p>
                    <Button onClick={() => navigate("/clientpanel/enagev1/templates?new=1")}>
                      Create your first template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    {filteredTemplates.map((t) => {
                      const selected = selectedTemplateId === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setSelectedTemplateId(t.id)}
                          className={`text-left rounded-lg border p-4 transition-colors ${
                            selected
                              ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                              : "border-border hover:border-primary/40"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-medium text-sm">{t.name || "Untitled"}</h3>
                            {t.type && (
                              <Badge variant="outline" className="text-xs capitalize">
                                {t.type}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
                            {t.subject_line || "No subject"}
                          </p>
                          {selected && (
                            <Badge className="mt-3 bg-primary text-primary-foreground">
                              <Check className="h-3 w-3 mr-1" /> Selected
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {step === 3 && selectedTemplate && (
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                    <span className="text-muted-foreground">To</span>
                    <span className="font-medium">{describeAudience(audience)} · {estimated.toLocaleString()} recipient{estimated === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                    <span className="text-muted-foreground">From</span>
                    <span className="font-medium">{merchant?.sender_email || <span className="text-destructive">Not configured — set in Settings</span>}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                    <Label htmlFor="subject" className="text-muted-foreground font-normal">Subject</Label>
                    <Input
                      id="subject"
                      value={subjectOverride}
                      onChange={(e) => setSubjectOverride(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2 items-center">
                    <Label htmlFor="campaign-name" className="text-muted-foreground font-normal">Name</Label>
                    <Input
                      id="campaign-name"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      placeholder="Internal campaign name"
                    />
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden">
                  <div className="bg-muted/30 px-4 py-2 border-b text-xs text-muted-foreground">Email preview</div>
                  <EmailPreviewIframe html={selectedTemplate.body_html || ""} minHeight={400} className="w-full bg-white" />
                </div>

                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  Sending to <span className="font-semibold">{estimated.toLocaleString()}</span> recipients will use{" "}
                  <span className="font-semibold">{estimated.toLocaleString()}</span> credits.{" "}
                  <span className="text-muted-foreground">
                    {Math.max(0, remaining - estimated).toLocaleString()} credits remaining after send.
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 4 && selectedTemplate && (
            <Card>
              <CardHeader>
                <CardTitle>Confirm & send</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!enoughCredits ? (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-destructive">Insufficient credits.</p>
                      <p className="mt-1 text-foreground/80">
                        You need {estimated.toLocaleString()} credits but have {remaining.toLocaleString()}. Upgrade your plan to continue.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border border-emerald-300/40 bg-emerald-50 dark:bg-emerald-950/20 p-4 text-sm">
                    <p className="font-medium text-emerald-800 dark:text-emerald-200">Ready to send</p>
                    <ul className="mt-2 space-y-1 text-foreground/80">
                      <li>• Audience: {describeAudience(audience)}</li>
                      <li>• Recipients: {estimated.toLocaleString()}</li>
                      <li>• Template: {selectedTemplate.name}</li>
                      <li>• From: {merchant?.sender_email}</li>
                      <li>• Subject: {subjectOverride}</li>
                      <li>
                        • Credits: {estimated.toLocaleString()} used, {(remaining - estimated).toLocaleString()} remaining
                      </li>
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setStep(3)} disabled={sending}>
                    Back
                  </Button>
                  <Button onClick={handleSend} disabled={!enoughCredits || sending || !merchant?.sender_email}>
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" /> Send Now
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Nav */}
        {step < 4 && (
          <div className="mt-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button onClick={() => setStep(step + 1)} disabled={!canNext}>
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {merchant && (
        <SelectCustomersDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          merchantId={merchant.id}
          initialSelected={audience.customer_ids || []}
          onConfirm={(ids) => setAudience({ ...audience, customer_ids: ids })}
        />
      )}
      </div>
  );
}
